import { Button, Collapse, Stack, TextField, Typography } from '@mui/material';
import { ChevronSmallDownIcon } from '@silbo/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../assets/images/logo.svg';
import BackButton from '../components/BackButton';
import { useHealthCenterContext } from '../contexts/HealthCenterContext';
import { useUserAppContext } from '../contexts/UserAppContext';

export const Route = createFileRoute('/health-center-link')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (!context.userAppContext.app) {
      throw redirect({ to: '/app-select' });
    }
    if (context.healthCenterContext.healthCenterConfig?.id) {
      throw redirect({ to: '/porter-login' });
    }
  },
});

function RouteComponent() {
  const { t } = useTranslation('patient-transport');
  const [healthCenterId, setHealthCenterId] = useState('');
  const [healthCenterQrCodeKey, setHealthCenterQrCodeKey] = useState('');
  const [devOpen, setDevOpen] = useState(import.meta.env.DEV);

  const { storeHealthCenterConfig } = useHealthCenterContext();
  const { resetApp } = useUserAppContext();

  const navigate = Route.useNavigate();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    storeHealthCenterConfig({ id: healthCenterId, key: healthCenterQrCodeKey });
    navigate({ to: '/porter-login' });
  };

  const handleClick = () => {
    navigate({ to: '/qr-code-scan' });
  };

  const handleBack = () => {
    resetApp();
    navigate({ to: '/app-select' });
  };

  return (
    <Stack spacing={12} paddingX={12} justifyContent="center" height="100%">
      <BackButton onClick={handleBack} />
      <Stack spacing={4} alignItems="center">
        <img src={logo} alt="logo" height={LOGO_HEIGHT} />
      </Stack>
      <Stack spacing={6} alignItems="center">
        <Typography align="center">{t('healthCenterLink.description')}</Typography>
        <Stack spacing={3} width="100%" maxWidth={600} alignItems="center">
          <Button onClick={handleClick} fullWidth>
            {t('qrCodeScan.buttonLabel')}
          </Button>
          <Typography variant="caption" align="center" color="secondary">
            {t('healthCenterLink.helpText')}
          </Typography>
        </Stack>
      </Stack>
      {import.meta.env.DEV && (
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            onClick={() => setDevOpen((open) => !open)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="caption" color="secondary">
              DEV
            </Typography>
            <ChevronSmallDownIcon
              color="secondary"
              sx={{
                transform: devOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Stack>
          <Collapse in={devOpen}>
            <Stack spacing={3} component="form" onSubmit={handleSubmit}>
              <TextField
                value={healthCenterId}
                onChange={(event) => setHealthCenterId(event.target.value)}
                label={t('healthCenterLink.idLabel')}
                fullWidth
              />
              <TextField
                fullWidth
                value={healthCenterQrCodeKey}
                onChange={(event) => setHealthCenterQrCodeKey(event.target.value)}
                label={t('healthCenterLink.qrCodeKeyLabel')}
              />
              <Button variant="outlined" type="submit">
                {t('healthCenterLink.submit')}
              </Button>
            </Stack>
          </Collapse>
        </Stack>
      )}
    </Stack>
  );
}

const LOGO_HEIGHT = 50;
